import moment from 'moment';
import _ from 'lodash';
import {Q} from '@nozbe/watermelondb';
import {intervalTypes} from 'src/hook-form-inputs/interval-type';
import database from 'src/providers/watermelondb';

type ExportIntervalArgs = {
  sets: any[];
  events?: any[]; // may be observables shape (event.set.id) or empty
  useObservables?: boolean;
  startDate?: string | number | Date;
  endDate?: string | number | Date;
  session?: any; // optional: used when a single session recap is exported
};

export const exportInterval = async ({
  sets = [],
  events = [],
  useObservables = false,
  startDate,
  endDate,
  session,
}: ExportIntervalArgs) => {
  const rows: any[] = [
    [
      'Session Date',
      'Interval Number',
      'Interval Type',
      'Time Stamp',
      'Behavior Occurred',
      'Intensity (If Toggled On)',
      'ABC (When Selected)',
    ],
  ];

  // --- helpers ---
  const val = (v: any, alt: any) => (v !== undefined && v !== null ? v : alt);
  const setStart = (s: any) =>
    val(s?.startTimestamp, s?.start_timestamp ?? s?.start);
  const setEnd = (s: any) => val(s?.endTimestamp, s?.end_timestamp ?? s?.end);
  const progId = (s: any) => val(s?.programId, s?.program_id ?? s?.program?.id);
  const sessId = (s: any) => val(s?.session?.id, s?.session_id);

  const normalizePromptsArray = (ps: any): any[] => {
    if (Array.isArray(ps)) {
      return ps;
    }
    if (ps == null) {
      return [];
    }
    if (typeof ps === 'string') {
      try {
        const parsed = JSON.parse(ps);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        if (ps.includes(',')) {
          return ps
            .split(',')
            .map((s: string) => s.trim())
            .filter(Boolean);
        }
        return [ps];
      }
    }
    return [];
  };

  // --- filter sets by date like the table (uses set.start_timestamp) ---
  const filteredSets =
    startDate && endDate
      ? sets.filter((s: any) => {
          const ts = Number(setStart(s)) || 0;
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          return ts >= start.getTime() && ts <= end.getTime();
        })
      : sets;

  // --- key events by set id (match table behavior: startDate || !useObservables => 'set_id', else 'set.id') ---
  const pickKey = (() => {
    const sample = (events || []).find(Boolean);
    if (sample) {
      if (sample?.set?.id != null) {
        return (e: any) => String(e?.set?.id ?? '');
      }
      if (sample?.set_id != null) {
        return (e: any) => String(e?.set_id ?? '');
      }
    }
    // fallback to the table's condition
    return startDate || !useObservables
      ? (e: any) => String(e?.set_id ?? '')
      : (e: any) => String(e?.set?.id ?? '');
  })();

  const eventsBySetId: Record<string, any> = _.keyBy(events || [], pickKey);

  // --- batch lookups: sessions (for session date) ---
  const sessionIds = _.uniq(
    filteredSets.map(sessId).filter(Boolean).map(String),
  );

  const sessionsById: Record<string, any> = {};
  if (sessionIds.length > 0) {
    for (const chunk of _.chunk(sessionIds, 500)) {
      const sess = await database
        .get('sessions')
        .query(Q.where('id', Q.oneOf(chunk)))
        .fetch();
      for (const s of sess) {
        sessionsById[s.id] = s;
      }
    }
  }

  // --- batch lookups: programs (for interval type) ---
  const programIds = _.uniq(
    filteredSets.map(progId).filter(Boolean).map(String),
  );

  const programsById: Record<string, any> = {};
  if (programIds.length > 0) {
    for (const chunk of _.chunk(programIds, 500)) {
      const progs = await database
        .get('programs')
        .query(Q.where('id', Q.oneOf(chunk)))
        .fetch();
      for (const p of progs) {
        programsById[p.id] = p;
      }
    }
  }

  // --- batch lookups: prompts (for ABC names) ---
  const collectPromptIds = (): string[] => {
    const ids = new globalThis.Set<string>();
    for (const s of filteredSets) {
      const e = eventsBySetId[String(s?.id ?? '')];
      if (!e) {
        continue;
      }
      const ps = normalizePromptsArray(e?.prompts);
      for (const p of ps) {
        if (typeof p === 'string') {
          ids.add(p);
        } else if (p?.id) {
          ids.add(p.id);
        }
      }
    }
    return Array.from(ids);
  };

  const promptNameById: Record<string, string> = {};
  const promptIds = collectPromptIds();

  const fetchByIdBatches = async (
    table: string,
    ids: string[],
    idField: string = 'id',
  ) => {
    const out: any[] = [];
    for (const ch of _.chunk(ids, 500)) {
      const _rows = await database
        .get(table)
        .query(Q.where(idField, Q.oneOf(ch)))
        .fetch();
      out.push(..._rows);
    }
    return out;
  };

  if (promptIds.length > 0) {
    // try prompts first
    const pr = await fetchByIdBatches('prompts', promptIds, 'id');
    for (const row of pr) {
      promptNameById[row.id] = row?.name || row?.acronym || '';
    }
    // fallback program_prompt
    const missing = promptIds.filter(id => !promptNameById[id]);
    if (missing.length > 0) {
      const ppr = await fetchByIdBatches('program_prompt', missing, 'id');
      for (const pp of ppr) {
        const n = pp?.name || pp?.acronym || '';
        if (n) {
          promptNameById[pp.id] = n;
        }
      }
      // hop via prompt_id -> prompts
      const stillMissing = missing.filter(id => !promptNameById[id]);
      if (stillMissing.length > 0 && ppr.length > 0) {
        const baseIds = _.uniq(
          ppr.map((x: any) => x?.prompt_id || x?.promptId).filter(Boolean),
        );
        if (baseIds.length > 0) {
          const basePrompts = await fetchByIdBatches('prompts', baseIds, 'id');
          const baseMap: Record<string, string> = {};
          for (const b of basePrompts) {
            baseMap[b.id] = b?.name || b?.acronym || '';
          }
          for (const pp of ppr) {
            const nm = baseMap[pp?.prompt_id || pp?.promptId];
            if (nm && !promptNameById[pp.id]) {
              promptNameById[pp.id] = nm;
            }
          }
        }
      }
    }
  }

  const namesForEventPrompts = (e: any): string[] => {
    const ps = normalizePromptsArray(e?.prompts);
    const out: string[] = [];
    for (const p of ps) {
      if (typeof p === 'string') {
        const n = promptNameById[p];
        if (n) {
          out.push(n);
        }
      } else if (p?.name) {
        out.push(p.name);
      } else if (p?.acronym) {
        out.push(p.acronym);
      } else if (p?.id && promptNameById[p.id]) {
        out.push(promptNameById[p.id]);
      }
    }
    return out;
  };

  // --- group sets by session id (both shapes) ---
  const groups: Record<string, any[]> = _.groupBy(filteredSets, s =>
    String(sessId(s) ?? ''),
  );

  const singleGroupId =
    Object.keys(groups).length === 1 ? Object.keys(groups)[0] : null;

  // --- build rows per session group ---
  for (const sid of Object.keys(groups)) {
    const sessionSets = groups[sid] || [];
    // session date: prefer DB 'sessions.date'; if single group and prop `session` exists, use session.sessionDate; else fallback to set.session_date
    const displaySessionDate = (() => {
      if (sessionsById[sid]?.session_date) {
        return moment(sessionsById[sid].session_date).format('MM/DD/YY');
      }
      if (
        singleGroupId &&
        session &&
        sid === singleGroupId &&
        session?.sessionDate
      ) {
        return moment(session.sessionDate).format('MM/DD/YY');
      }
      // fallback to the first set’s session_date if present
      const fallback = sessionSets.find(s => s?.session_date)?.session_date;
      return fallback ? moment(fallback).format('MM/DD/YY') : '';
    })();

    let sumY = 0;

    for (let i = 0; i < sessionSets.length; i++) {
      const set = sessionSets[i];
      const event = eventsBySetId[String(set?.id ?? '')];

      if (event?.value === 'Y') {
        sumY++;
      }

      const p = programsById[String(progId(set) ?? '')];
      const intervalValue = p?.interval;
      const intervalLabel =
        intervalTypes.find((it: any) => it.value === intervalValue)?.label ||
        intervalValue ||
        '';

      const startTs = setStart(set);
      const endTs = setEnd(set);

      const timeStamp =
        `${startTs ? moment(startTs).format('hh:mm:ss A') : ''} - ` +
        `${endTs ? moment(endTs).format('hh:mm:ss A') : 'Incomplete'}`;

      const abc = event
        ? namesForEventPrompts(event)
            .map(n => (typeof n === 'string' ? n.replaceAll('"', "'") : ''))
            .filter(Boolean)
            .join('\n')
        : '';

      rows.push([
        i === 0 ? displaySessionDate : '', // only for the first row of the session
        `${i + 1}`, // Interval Number
        intervalLabel, // Interval Type
        timeStamp, // Time Stamp
        event?.value || '', // Behavior Occurred (Y/N/-)
        _.capitalize(event?.intensity || ''), // Intensity
        abc, // ABC
      ]);
    }

    // trailing summary row for this session (% Y)
    const percentY =
      sessionSets.length > 0
        ? `${Math.ceil((sumY / sessionSets.length) * 100)}%`
        : '0%';

    rows.push(['', '', '', '', percentY, '', '']);
  }

  return rows;
};
